
import { LeaderboardItem, SupabaseLeaderboardItem } from "../types";
import { Account, Geography, Product, SalesTransaction } from "./types";

/**
 * Maps Supabase leaderboard data to our Account model
 */
export function mapToAccounts(leaderboardData: LeaderboardItem[]): Account[] {
  const accountsMap = new Map<string, Account>();
  
  leaderboardData.forEach(item => {
    if (!item.accountName) return;
    
    const id = `${item.accountName}-${item.id}`;
    
    if (!accountsMap.has(id)) {
      accountsMap.set(id, {
        id,
        name: item.accountName,
        country: item.country || 'Unknown',
        region: item.region,
        industry: item.industry
      });
    }
  });
  
  return Array.from(accountsMap.values());
}

/**
 * Maps Supabase leaderboard data to our Product model
 */
export function mapToProducts(leaderboardData: LeaderboardItem[]): Product[] {
  const productsMap = new Map<string, Product>();
  
  leaderboardData.forEach(item => {
    const id = `${item.productLine}-${item.id}`;
    
    if (!productsMap.has(id)) {
      productsMap.set(id, {
        id,
        name: `${item.productLine} ${item.id}`,
        productLine: item.productLine,
        category: item.industry // Using industry as a proxy for category
      });
    }
  });
  
  return Array.from(productsMap.values());
}

/**
 * Maps Supabase leaderboard data to our Geography model
 */
export function mapToGeographies(leaderboardData: LeaderboardItem[]): Geography[] {
  const geographiesMap = new Map<string, Geography>();
  
  leaderboardData.forEach(item => {
    const key = `${item.country || 'Unknown'}-${item.region}`;
    
    if (!geographiesMap.has(key)) {
      geographiesMap.set(key, {
        country: item.country || 'Unknown',
        region: item.region,
        salesArea: item.region // Using region as a proxy for sales area
      });
    }
  });
  
  return Array.from(geographiesMap.values());
}

/**
 * Maps Supabase leaderboard data to our SalesTransaction model
 */
export function mapToSalesTransactions(leaderboardData: LeaderboardItem[]): SalesTransaction[] {
  return leaderboardData.map(item => {
    const accountId = `${item.accountName}-${item.id}`;
    const productId = `${item.productLine}-${item.id}`;
    
    return {
      invoiceId: `INV-${item.id}`,
      date: new Date().toISOString(), // Using current date as a placeholder
      accountId,
      productId,
      quantity: 1,
      revenue: item.achievement,
      shipToCountry: item.country || 'Unknown',
      attributionType: 'Direct' // Defaulting to Direct attribution
    };
  });
}
